import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, isSuperAdmin } from "@/lib/auth";
import { toast } from "sonner";
import { InlineDeleteButton } from "@/components/InlineDeleteButton";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Lock, Plus, RefreshCw, Search, ShieldAlert, Trash2 } from "lucide-react";

// ---------------------------------------------------------------------------
// MP12 Module 6B — Restricted Records Desk.
// A generic index/boundary layer. Creating a row here does NOT rewrite the
// underlying canonical record; other endpoints can consult this collection to
// filter restricted rows out of ordinary queues for unauthorised viewers.
// ---------------------------------------------------------------------------

function canView(user) {
  return !!user && ["super_admin", "gfmp_reviewer", "catalog_manager"].includes(user.role);
}
function canWrite(user) {
  return !!user && ["super_admin", "gfmp_reviewer"].includes(user.role);
}

function NewRestrictedDialog({ vocab, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    record_type: "track", record_id: "",
    restriction_category: "OTHER", restriction_reason: "",
    handling_instructions: "", authorised_access_note: "",
    linked_identity_map_id: "",
  });
  const submit = async () => {
    if (!form.record_id.trim() || !form.record_type.trim()) {
      toast.error("record_type and record_id are required."); return;
    }
    setSaving(true);
    try {
      const r = await api.post("/restricted-records", form);
      onCreated?.(r.data);
      toast.success("Restricted classification recorded.");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="rr-new" className="gap-2"><Plus className="w-4 h-4" /> Flag record</Button></DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Flag Record as Restricted</DialogTitle>
          <DialogDescription>Adds a restricted-record classification. Does NOT rewrite the underlying record. Kandake will consult this row to filter the record out of ordinary queues for unauthorised viewers.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Record type</Label><Input value={form.record_type} onChange={(e)=>setForm({...form, record_type: e.target.value})} placeholder="track / sync_brief / communication…" data-testid="rr-type" /></div>
          <div><Label>Record id</Label><Input value={form.record_id} onChange={(e)=>setForm({...form, record_id: e.target.value})} data-testid="rr-id" /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.restriction_category} onValueChange={(v)=>setForm({...form, restriction_category: v})}>
              <SelectTrigger data-testid="rr-category"><SelectValue /></SelectTrigger>
              <SelectContent>{(vocab.categories || []).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Linked identity map id</Label><Input value={form.linked_identity_map_id} onChange={(e)=>setForm({...form, linked_identity_map_id: e.target.value})} placeholder="optional" /></div>
          <div className="col-span-2"><Label>Reason</Label><Textarea rows={2} value={form.restriction_reason} onChange={(e)=>setForm({...form, restriction_reason: e.target.value})} data-testid="rr-reason" /></div>
          <div className="col-span-2"><Label>Handling instructions</Label><Textarea rows={2} value={form.handling_instructions} onChange={(e)=>setForm({...form, handling_instructions: e.target.value})} /></div>
          <div className="col-span-2"><Label>Authorised-access note</Label><Textarea rows={2} value={form.authorised_access_note} onChange={(e)=>setForm({...form, authorised_access_note: e.target.value})} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="rr-save">{saving ? "Saving…" : "Flag"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RestrictedRecordsDesk() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [recordType, setRecordType] = useState("");
  const [category, setCategory] = useState("");
  const [vocab, setVocab] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (q.trim()) params.q = q.trim();
      if (recordType) params.record_type = recordType;
      if (category) params.category = category;
      const r = await api.get("/restricted-records", { params });
      setRows(r.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed.");
    } finally { setLoading(false); }
  }, [q, recordType, category]);

  useEffect(() => {
    if (!canView(user)) { setLoading(false); return; }
    api.get("/restricted-records/meta/vocab").then((r)=>setVocab(r.data || {})).catch(()=>{});
    load();
  }, [user, load]);

  const remove = async (id) => {
    if (!isSuperAdmin(user)) return;
    if (!window.confirm("Delete this restricted-record classification? (record itself is unaffected)")) return;
    try { await api.delete(`/restricted-records/${id}`); setRows((rs)=>rs.filter((r)=>r.id !== id)); toast.success("Deleted."); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed."); }
  };

  if (!canView(user)) {
    return <div className="p-8 text-sm text-muted-foreground">Access to restricted-record classifications is limited to authorised internal roles.</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="restricted-records-desk">
      <div className="flex flex-wrap items-center gap-3">
        <Lock className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Restricted Records</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Governance index. Marks a canonical record as restricted so ordinary queues and future Kingswork consumer views can exclude it. Existing Communications classifications (STANDARD / CONFIDENTIAL / RESTRICTED) remain independent.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">Internal-only</Badge>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="rr-refresh"><RefreshCw className="w-4 h-4" /> Refresh</Button>
        {canWrite(user) && <NewRestrictedDialog vocab={vocab} onCreated={(x)=>setRows((rs)=>[x, ...rs])} />}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> Filters</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><Label>Search</Label><Input value={q} onChange={(e)=>setQ(e.target.value)} data-testid="rr-filter-q" /></div>
            <div><Label>Record type</Label><Input value={recordType} onChange={(e)=>setRecordType(e.target.value)} placeholder="any" data-testid="rr-filter-type" /></div>
            <div>
              <Label>Category</Label>
              <Select value={category || "__any__"} onValueChange={(v)=>setCategory(v === "__any__" ? "" : v)}>
                <SelectTrigger data-testid="rr-filter-category"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any</SelectItem>
                  {(vocab.categories || []).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end"><Button className="w-full" onClick={load} data-testid="rr-filter-apply">Apply</Button></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Classifications ({rows.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
            rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="rr-empty">No restricted classifications recorded.</p> :
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.id} className="border border-border rounded-md px-4 py-3" data-testid={`rr-row-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{r.record_type}</Badge>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{r.restriction_category}</Badge>
                    <span className="font-mono text-xs">{r.record_id}</span>
                    <span className="text-sm flex-1">{r.restriction_reason || "(no reason)"}</span>
                    {isSuperAdmin(user) && (
                      <InlineDeleteButton
                        endpoint={`/restricted-records/${r.id}`}
                        recordLabel="Restricted Record"
                        recordId={r.id}
                        isSuperAdmin={isSuperAdmin(user)}
                        variant="outline"
                        onDeleted={(id) => setRows((rs) => rs.filter((x) => x.id !== id))}
                        testid={`rr-delete-${r.id}`}
                      />
                    )}
                  </div>
                  {r.handling_instructions && <div className="text-xs mt-2"><span className="opacity-60">Handling:</span> {r.handling_instructions}</div>}
                  {r.authorised_access_note && <div className="text-xs mt-1"><span className="opacity-60">Authorised access:</span> {r.authorised_access_note}</div>}
                  {r.linked_identity_map_id && <div className="text-xs mt-1"><span className="opacity-60">Linked identity map:</span> <span className="font-mono">{r.linked_identity_map_id}</span></div>}
                </div>
              ))}
            </div>
          }
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground flex items-center gap-2">
        <ShieldAlert className="w-3.5 h-3.5" />
        Restricted records are excluded from any future Kingswork consumer views by default. Existing Communications Hub classifications are unaffected.
      </p>
    </div>
  );
}
